import { CommonModule } from '@angular/common';
import { ApiOrderCancellation, OrderStatus } from '../../models/order.models';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { OrderUtils } from '../../utils/order.utils';
import { FAMILY_PRIMARY_TAG_LABEL, FAMILY_TAG_LABEL } from '../../../shared/utils/family-tags.util';

/** Minimal row shape shared by admin/staff orders tables. */
export interface OrdersListTableRow {
  id: string;
  orderId?: string | null;
  licensePlate?: string | null;
  laneName?: string | null;
  status?: { label?: string | null } | null;
  dateTime?: string | null;
  customerName?: string | null;
  userName?: string | null;
  washType?: string | null;
  washTypeValue?: string | null;
  /** Add-on chips rendered beside the wash type, in display order (CWS-1125). */
  addOnLabels?: string[] | null;
  price?: string | null;
  /** CWS-1444: the plan's current recurring price, when the membership changed plan. */
  currentPlanPrice?: string | null;
  /** CWS-1444: true when the membership has moved plan since this order was sold. */
  planChanged?: boolean | null;
  /** CWS-1582 §8: Family / Primary tags beside the package name. */
  isFamily?: boolean | null;
  isFamilyPrimary?: boolean | null;
  payment_method?: string | null;
  phone?: string | null;
  listPrice?: string | null;
  voucherCode?: string | null;
  discount?: string | null;
  discountId?: string | null;
  discountName?: string | null;
  email?: string | null;
  source?: string | null;
  /** CWS-1781: the package / book / plan name shown beside the wash type. For a changed membership this is the CURRENT plan. */
  packageName?: string | null;
  /** CWS-1444: the plan the order was sold on, only when it differs from `packageName`. */
  soldPackageName?: string | null;
  /** CWS-1777: Paid / Partially refunded / Refunded as the server derived it. */
  paymentStatusDisplay?: string | null;
  /** CWS-1779: behind the badge — what was collected and what was refunded, in dollars. */
  paidTotal?: number | null;
  refundedTotal?: number | null;
  /** The raw `payment_status`, the fallback when the API did not derive a display value. */
  paymentStatus?: string | null;
  /** CWS-1781: created date & time. */
  createdAt?: string | null;
  /** CWS-1771: what the row may offer; drives the row actions menu. */
  cancellation?: ApiOrderCancellation | null;
}

/**
 * Shared orders data table used by admin-orders and staff-orders to avoid
 * duplicating the large HTML table markup (CodeAnt duplicate-density).
 */
@Component({
  selector: 'app-orders-list-table',
  standalone: true,
  imports: [CommonModule, RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './orders-list-table.component.html',
  host: {
    class: 'orders-list-table-host',
    style: 'display: block; width: 100%;',
  },
})
export class OrdersListTableComponent {
  /** Family plan tag text (CWS-1582 §8). */
  readonly familyTagLabel = FAMILY_TAG_LABEL;
  readonly familyPrimaryLabel = FAMILY_PRIMARY_TAG_LABEL;

  @Input({ required: true }) orders: OrdersListTableRow[] = [];
  @Input({ required: true }) getSortIcon!: (field: string) => string;
  @Input({ required: true }) getStatusClass!: (status: OrdersListTableRow['status']) => string;
  @Input({ required: true }) getWashTypeClass!: (
    washType: string | null | undefined,
    washTypeValue: string | null | undefined
  ) => string;
  @Input({ required: true }) formatPaymentMethod!: (method: string | null | undefined) => string;
  @Input({ required: true }) getCapitalizedSource!: (source: string | null | undefined) => string;
  @Input({ required: true }) canShowActions!: (order: OrdersListTableRow) => boolean;
  /** When true, discount names with an id render as links to the Discount summary. */
  @Input() canOpenDiscount = false;

  /**
   * Chip colour for an add-on label. A plain method rather than another required Input:
   * it is pure presentation off the label and needs no service (CWS-1125).
   */
  getAddOnLabelClass(label: string): string {
    return OrderUtils.getAddOnLabelClass(label);
  }

  @Output() sortByOrderId = new EventEmitter<void>();
  @Output() sortByDateTime = new EventEmitter<void>();
  @Output() openOrderDetails = new EventEmitter<OrdersListTableRow>();
  @Output() toggleDropdown = new EventEmitter<{ event: Event; orderId: string }>();

  onToggleDropdown(event: Event, orderId: string): void {
    this.toggleDropdown.emit({ event, orderId });
  }

  /**
   * CWS-1777: Paid / Partially refunded / Refunded, named and coloured.
   * CWS-1779: a refund still owed outranks the derived payment state on the badge.
   */
  getPaymentStatusBadge(order: OrdersListTableRow): OrderStatus {
    if (order.cancellation?.refund_failed) {
      return { label: 'Refund failed', value: 'refund_failed', color: 'red' };
    }
    return OrderUtils.paymentStatusBadge(order.paymentStatusDisplay, order.paymentStatus);
  }

  /**
   * CWS-1779: on hover, what the Partially refunded / Refunded badge stands
   * for — "Refunded $12.00 of $36.00". Null for any other state.
   */
  getPaymentStatusHint(order: OrdersListTableRow): string | null {
    const value = this.getPaymentStatusBadge(order).value;
    if (value !== 'partially_refunded' && value !== 'refunded') return null;
    const refunded = order.refundedTotal;
    if (refunded === null || refunded === undefined) return null;
    const paid = order.paidTotal;
    const money = (n: number) => `$${n.toFixed(2)}`;
    return paid !== null && paid !== undefined && paid > 0
      ? `Refunded ${money(refunded)} of ${money(paid)}`
      : `Refunded ${money(refunded)}`;
  }

  getPaymentStatusClass(order: OrdersListTableRow): string {
    return `status-${this.getPaymentStatusBadge(order).color}`;
  }

  hasDiscountLink(order: OrdersListTableRow): boolean {
    return Boolean(this.canOpenDiscount && order.discountId && order.discountName);
  }

  /**
   * CWS-1444: the amount shown in the Order Amount column.
   *
   * For a membership whose plan has changed this is the plan's CURRENT price,
   * not `final_price` — Product asked for the latest package amount here.
   *
   * Note this makes the column stop reconciling with the revenue tiles above
   * the list: `todays_revenue` sums `orders.final_price` server-side, which is
   * what was actually collected and what the period's revenue is built from. A
   * membership sold at $25 and downgraded to $10 therefore contributes $25 to
   * the tile and displays $10 here. That is a deliberate product choice, not a
   * drift between two calculations.
   */
  getOrderAmount(order: OrdersListTableRow): string {
    if (order.planChanged && order.currentPlanPrice) {
      return order.currentPlanPrice;
    }
    return order.price || '-';
  }
}
